import React, { useState } from "react";
import toast from "react-hot-toast";

export interface VehicleFormData {
  assignedDriver: string;
  brand: string;
  color: string;
  fuel_type: string;
  id: string;
  insurance_number: string;
  max_load: string;
  mileage: string;
  model: string;
  plate_number: string;
  registrationDocument?: File;
  status: "active" | "inactive" | "maintenance";
  type: string;
  year: string;
}

interface EditVehicleModalProps {
  drivers: Array<{ id: string; name: string }>;
  initialData: VehicleFormData;
  onClose: () => void;
  onSubmit: (data: VehicleFormData, id: string) => Promise<void>;
  open: boolean;
}

interface InputFieldProps {
  className?: string;
  id: string;
  label: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  value: string;
}

const InputField = ({
  className,
  id,
  label,
  onChange,
  required = false,
  type = "text",
  value,
}: InputFieldProps) => (
  <div className={`relative ${className || ""}`}>
    <input
      className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
      id={id}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      type={type}
      value={value}
    />
    <label
      className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
      htmlFor={id}
    >
      {label}
    </label>
  </div>
);

const EditVehicleModal: React.FC<EditVehicleModalProps> = ({
  drivers,
  initialData,
  onClose,
  onSubmit,
  open,
}) => {
  const [form, setForm] = React.useState<VehicleFormData>(() => ({
    ...initialData,
    assignedDriver: initialData.assignedDriver
      ? initialData.assignedDriver.toString()
      : "",
    id: initialData.id,
  }));
  const [initialized, setInitialized] = React.useState(false);

  // Only initialize form state from initialData when modal is first opened
  React.useEffect(() => {
    if (open && !initialized) {
      setForm({
        ...initialData,
        assignedDriver: initialData.assignedDriver
          ? initialData.assignedDriver.toString()
          : "",
        id: initialData.id,
      });
      setCustomColor(initialData.color === "Other" ? initialData.color : "");
      setCustomFuel(
        initialData.fuel_type === "Other" ? initialData.fuel_type : "",
      );
      setInitialized(true);
    }
    if (!open && initialized) {
      setInitialized(false);
    }
  }, [open, initialized]);
  const [submitting, setSubmitting] = React.useState(false);
  const [customColor, setCustomColor] = React.useState(
    form.color === "Other" ? form.color : "",
  );
  const [customFuel, setCustomFuel] = React.useState(
    form.fuel_type === "Other" ? form.fuel_type : "",
  );
  // Preview URL for existing registration document
  const [previewUrl, setPreviewUrl] = React.useState<null | string>(null);
  // Load document on mount if present in initialData
  React.useEffect(() => {
    const docUrl =
      (initialData as any).registrationDocumentUrl ||
      (initialData as any).registration_document_url ||
      (initialData as any).registration_document ||
      null;
    setPreviewUrl(docUrl);
  }, [initialData]);

  React.useEffect(() => {
    // No need to fetch drivers here, they are passed as props
  }, []);

  if (!open) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setForm({ ...form, registrationDocument: e.target.files[0] });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        // Only close if clicking the backdrop, not the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900 md:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", overflowY: "auto", width: "80vw" }}
      >
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Edit Vehicle
        </h2>
        <form
          className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setSubmitting(true);
            let errorMsg = "";
            if (parseInt(form.year) > 2026) {
              errorMsg = "Year must not be greater than 2026.";
            }
            if (!form.plate_number || form.plate_number.trim() === "") {
              errorMsg = errorMsg ? errorMsg + "\n" : "";
              errorMsg += "Plate number is required.";
            }
            if (!form.mileage || form.mileage.toString().trim() === "") {
              errorMsg = errorMsg ? errorMsg + "\n" : "";
              errorMsg += "Mileage is required.";
            } else if (
              isNaN(Number(form.mileage)) ||
              !Number.isInteger(Number(form.mileage)) ||
              Number(form.mileage) < 0
            ) {
              errorMsg = errorMsg ? errorMsg + "\n" : "";
              errorMsg += "Mileage must be a positive integer.";
            }
            if (errorMsg) {
              toast.error(errorMsg);
              setSubmitting(false);
              return;
            }
            try {
              let payload: any;
              // Debug: log assigned driver value before submit
              console.log(
                "Assigned driver before submit:",
                form.assignedDriver,
              );
              const assignedDriverValue =
                form.assignedDriver && form.assignedDriver !== ""
                  ? String(form.assignedDriver)
                  : null;
              if (form.registrationDocument instanceof File) {
                const fd = new FormData();
                fd.append("id", form.id ?? "");
                fd.append("brand", form.brand ?? "");
                fd.append("model", form.model ?? "");
                fd.append("year", form.year ?? "");
                fd.append("type", form.type ?? "");
                fd.append("color", form.color ?? "");
                fd.append("plate_number", form.plate_number ?? "");
                fd.append("fuel_type", form.fuel_type ?? "");
                fd.append("max_load", form.max_load ?? "");
                fd.append(
                  "mileage",
                  form.mileage && !isNaN(Number(form.mileage))
                    ? String(parseInt(form.mileage, 10))
                    : "0",
                );
                fd.append("assigned_driver_id", assignedDriverValue ?? "");
                fd.append("insurance_number", form.insurance_number ?? "");
                fd.append("status", form.status ?? "active");
                fd.append("registration_document", form.registrationDocument);
                // Debug: print FormData before sending
                const debugData: Record<string, any> = {};
                fd.forEach((value, key) => {
                  debugData[key] = value;
                });

                console.log(
                  "[EditVehicleModal] FormData to be sent:",
                  debugData,
                );
                payload = fd;
              } else {
                // Ensure all fields are included in the payload
                payload = {
                  assignedDriver: form.assignedDriver ?? "20",
                  brand: form.brand ?? "",
                  color: form.color ?? "",
                  fuel_type: form.fuel_type ?? "",
                  id: form.id ?? "",
                  insurance_number: form.insurance_number ?? "",
                  max_load: form.max_load ?? "",
                  mileage:
                    form.mileage && !isNaN(Number(form.mileage))
                      ? String(parseInt(form.mileage, 10))
                      : "0",
                  model: form.model ?? "",
                  plate_number: form.plate_number ?? "",
                  status: form.status ?? "active",
                  type: form.type ?? "",
                  year: form.year ?? "",
                };

                // Log all fields for debug
                console.log("[EditVehicleModal] JSON to be sent:", payload);
              }
              await onSubmit(payload, form.id ?? "");
              toast.success("Vehicle updated successfully!");
              setSubmitting(false);
            } catch (error) {
              toast.error("Failed to update vehicle. Please try again.");
              setSubmitting(false);
            }
          }}
        >
          {/* Brand */}
          <InputField
            className="w-full"
            id="vehicle-brand"
            label="Brand"
            onChange={(value) => setForm({ ...form, brand: value })}
            value={form.brand}
          />
          {/* Model */}
          <InputField
            className="w-full"
            id="vehicle-model"
            label="Model"
            onChange={(value) => setForm({ ...form, model: value })}
            value={form.model}
          />
          {/* Year (select) */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-year"
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              required
              value={form.year}
            >
              <option value="">Select Year</option>
              {Array.from({ length: 2026 - 1990 + 1 }, (_, i) => 1990 + i).map(
                (year) => (
                  <option key={year} value={year.toString()}>
                    {year}
                  </option>
                ),
              )}
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-year"
            >
              Year
            </label>
          </div>
          {/* Type */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-type"
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              required
              value={form.type}
            >
              <option value="">Select Type</option>
              <option value="Bike">Bike</option>
              <option value="Motorcycle">Motorcycle</option>
              <option value="Sedan">Sedan</option>
              <option value="Hatchback">Hatchback</option>
              <option value="SUV">SUV</option>
              <option value="Mini Van">Mini Van</option>
              <option value="Cargo Van">Cargo Van</option>
              <option value="Service Van">Service Van</option>
              <option value="Pickup">Pickup</option>
              <option value="Light Truck">Light Truck</option>
              <option value="Utility Truck">Utility Truck</option>
              <option value="Minibus">Minibus</option>
              <option value="Bus">Bus</option>
              <option value="Luxury Car">Luxury Car</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-type"
            >
              Type
            </label>
          </div>
          {/* Color (select + custom) */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-color"
              onChange={(e) => {
                setForm({ ...form, color: e.target.value, id: form.id });
                if (e.target.value === "Other") setCustomColor("");
              }}
              required
              value={form.color}
            >
              <option value="">Select Color</option>
              <option value="White">White</option>
              <option value="Black">Black</option>
              <option value="Silver">Silver</option>
              <option value="Blue">Blue</option>
              <option value="Red">Red</option>
              <option value="Gray">Gray</option>
              <option value="Green">Green</option>
              <option value="Yellow">Yellow</option>
              <option value="Other">Other</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-color"
            >
              Color
            </label>
            {form.color === "Other" && (
              <input
                className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                onChange={(e) => {
                  setCustomColor(e.target.value);
                  setForm({ ...form, color: e.target.value, id: form.id });
                }}
                placeholder="Enter custom color"
                required
                value={customColor}
              />
            )}
          </div>
          {/* Plate */}
          <InputField
            className="w-full"
            id="vehicle-plate"
            label="Plate Number"
            onChange={(value) => setForm({ ...form, plate_number: value })}
            required={true}
            value={form.plate_number}
          />
          {/* Fuel Type (select + custom) */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-fuel"
              onChange={(e) => {
                setForm({ ...form, fuel_type: e.target.value, id: form.id });
                if (e.target.value === "Other") setCustomFuel("");
              }}
              required
              value={form.fuel_type}
            >
              <option value="">Select Fuel Type</option>
              <option value="Petrol">Petrol</option>
              <option value="Diesel">Diesel</option>
              <option value="Electric">Electric</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Other">Other</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-fuel"
            >
              Fuel Type
            </label>
            {form.fuel_type === "Other" && (
              <input
                className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                onChange={(e) => {
                  setCustomFuel(e.target.value);
                  setForm({ ...form, fuel_type: e.target.value, id: form.id });
                }}
                placeholder="Enter custom fuel type"
                required
                value={customFuel}
              />
            )}
          </div>
          {/* Max Load */}
          <InputField
            className="w-full"
            id="vehicle-maxload"
            label="Max Load (kg)"
            onChange={(value) => setForm({ ...form, max_load: value })}
            type="number"
            value={form.max_load}
          />
          {/* Mileage */}
          <InputField
            className="w-full"
            id="vehicle-mileage"
            label="Mileage (km)"
            onChange={(value) => setForm({ ...form, mileage: value })}
            required={true}
            type="number"
            value={form.mileage || ""}
          />
          {/* Assigned Driver (select) */}
          <div className="relative w-full">
            <select
              className="peer w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-driver"
              onChange={(e) => {
                setForm({
                  ...form,
                  assignedDriver: e.target.value,
                  id: form.id,
                });
              }}
              value={form.assignedDriver ? String(form.assignedDriver) : ""}
            >
              <option
                className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                value=""
              >
                Select Driver (Optional)
              </option>
              {drivers.map((driver) => (
                <option
                  className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                  key={driver.id}
                  value={driver.id}
                >
                  {driver.name}
                </option>
              ))}
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-driver"
            >
              Assigned Driver
            </label>
          </div>
          {/* Insurance Number */}
          <InputField
            className="w-full"
            id="vehicle-insurance"
            label="Insurance Number"
            onChange={(value) => setForm({ ...form, insurance_number: value })}
            value={form.insurance_number}
          />
          {/* Status */}
          <div className="relative md:col-span-2">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="vehicle-status"
              onChange={(e) =>
                setForm({
                  ...form,
                  id: form.id,
                  status: e.target.value as VehicleFormData["status"],
                })
              }
              required
              value={form.status}
            >
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="vehicle-status"
            >
              Status
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2 md:col-span-2">
            <button
              className="rounded bg-gray-200 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              disabled={submitting}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditVehicleModal;
